import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import { DataTable, DataTableColumn, EmptyState, PageHeader } from '@routes-plan/ui';
import PermissionButton from '../../../components/common/PermissionButton';
import StandardModal, { useModalLoading } from '../../../components/modales/StandardModal';
import StockTransferNew from './StockTransferNew';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { nowOperationalDateString } from '../../../utils/operationalDateTime';
import DateRangeOfficeFilter, { RangeFilter, filterParams } from '../shared/DateRangeOfficeFilter';
import { INVENTORY_ROUTES, NamedRef, OfficeOption, STOCK_STATUS_LABEL, ShowAlerts, StockStatus, apiErrorMessage, fetchOffices, formatDate, locationLabel } from '../shared/inventoryApi';
import { PageBody, useInventoryAlert } from '../shared/ui';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';

type Transfer = {
  id: number;
  date: string;
  originOffice?: NamedRef | null;
  originRoute?: NamedRef | null;
  destinationOffice?: NamedRef | null;
  destinationRoute?: NamedRef | null;
  items?: { product?: { title?: string } | null; quantity: number; status?: StockStatus }[];
  observation?: string | null;
};

// Mismo texto "Ruta · X" / "Sede · X" de locationLabel, en el idioma de la persona.
const side = (office?: NamedRef | null, route?: NamedRef | null) => locationLabel({ office, route });

const itemsText = (r: Transfer) =>
  (r.items || [])
    .map((i) => `${i.product?.title ?? ''} × ${i.quantity}${i.status && i.status !== 'DISPONIBLE' ? ` (${STOCK_STATUS_LABEL[i.status] ?? i.status})` : ''}`)
    .join(', ');

const StockTransferList: React.FC<ShowAlerts> = () => {
  const t = useT();
  const showAlert = useInventoryAlert();
  const { canTransfer, canRead } = useResourcePermissions(INVENTORY_ROUTES.transfers);
  const [offices, setOffices] = useState<OfficeOption[]>([]);
  const [filter, setFilter] = useState<RangeFilter>({ startDate: `${nowOperationalDateString().slice(0, 8)}01`, endDate: nowOperationalDateString(), officeId: '' });
  const [rows, setRows] = useState<Transfer[]>([]);
  const [loading, setLoading] = useState(true);
  // Nuevo traslado en el modal estándar (la página /new se mantiene).
  const [addOpen, setAddOpen] = useState(false);
  const addLoading = useModalLoading();
  setPageTitle('inventory.transfers.pageTitle');

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('stock-transfers', { params: filterParams(filter) });
      setRows(Array.isArray(data?.data) ? data.data : []);
    } catch (error) {
      showAlert(apiErrorMessage(error, t('inventory.transfers.loadError')), 'error');
    } finally {
      setLoading(false);
    }
  }, [filter, showAlert, t]);

  useEffect(() => {
    fetchOffices().then(setOffices).catch(() => setOffices([]));
  }, []);
  useEffect(() => {
    if (canRead) search();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead]);

  const columns: DataTableColumn<Transfer>[] = [
    { id: 'id', header: '#', value: (r) => r.id, sortable: true },
    { id: 'date', header: t('common.date'), value: (r) => formatDate(r.date), sortable: true },
    { id: 'origin', header: t('inventory.common.origin'), value: (r) => side(r.originOffice, r.originRoute), sortable: true },
    { id: 'destination', header: t('inventory.common.destination'), value: (r) => side(r.destinationOffice, r.destinationRoute), sortable: true },
    {
      id: 'items',
      header: t('inventory.common.products'),
      wrap: true,
      value: itemsText,
      render: (r) => (
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
          {(r.items || []).map((i, n) => (
            <Typography component="li" variant="body2" key={n}>
              {i.product?.title ?? ''} <Box component="span" sx={{ color: 'text.secondary' }}>× {i.quantity}</Box>
              {i.status && i.status !== 'DISPONIBLE' ? (
                <Box component="span" sx={{ color: 'warning.main' }}>{` · ${STOCK_STATUS_LABEL[i.status] ?? i.status}`}</Box>
              ) : null}
            </Typography>
          ))}
        </Box>
      ),
    },
    { id: 'observation', header: t('inventory.common.observation'), value: (r) => r.observation ?? '', wrap: true, hideBelow: 'md' },
  ];

  const newButton = (
    <PermissionButton
      allowed={canTransfer}
      deniedTooltip={t('inventory.transfers.noPermission')}
      variant="contained"
      startIcon={<SwapHorizOutlinedIcon />}
      onClick={() => setAddOpen(true)}
    >
      {t('inventory.transfers.newTransfer')}
    </PermissionButton>
  );

  return (
    <PageBody>
      <PageHeader title={t('inventory.transfers.title')} description={t('inventory.transfers.description')} actions={newButton} />
      <Card>
        <CardContent>
          <DateRangeOfficeFilter offices={offices} value={filter} onChange={setFilter} onSearch={search} loading={loading} />
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <DataTable<Transfer>
            rows={rows}
            columns={columns}
            getRowId={(r) => r.id}
            loading={loading}
            ariaLabel={t('inventory.transfers.title')}
            searchPlaceholder={t('inventory.transfers.search')}
            exportFileName={t('inventory.transfers.exportFile')}
            initialSort={{ columnId: 'id', direction: 'desc' }}
            emptyState={
              <EmptyState
                icon={<SwapHorizOutlinedIcon fontSize="inherit" />}
                title={t('inventory.transfers.empty')}
                description={t('inventory.transfers.emptyHint')}
                action={canTransfer ? newButton : undefined}
              />
            }
          />
        </CardContent>
      </Card>
      <StandardModal open={addOpen} onClose={() => setAddOpen(false)} title={t('inventory.transfers.newTransfer')} maxWidth={1000} loading={addLoading.loading} composed>
        {addOpen && (
          <StockTransferNew
            variant="modal"
            onClose={() => setAddOpen(false)}
            onSaved={() => {
              setAddOpen(false);
              search();
            }}
            showAlert={showAlert}
            showLoading={addLoading.showLoading}
            hideLoading={addLoading.hideLoading}
          />
        )}
      </StandardModal>
    </PageBody>
  );
};

export default StockTransferList;
